import React from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (tags: string[], notes: string, feedback?: 'positive' | 'negative') => void | Promise<void>;
  allowedTags?: string[];
  notesOnly?: boolean;
  defaultTags?: string[];
  defaultNotes?: string;
  showFeedbackSelector?: boolean;
  defaultFeedback?: 'positive' | 'negative';
}

const DEFAULT_TAGS = [
  'bugfix',
  'wishlist feature',
  'helpfulness',
  'tone',
  'correctness',
  'safety'
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  allowedTags = DEFAULT_TAGS, 
  notesOnly = false, 
  defaultTags, 
  defaultNotes,
  showFeedbackSelector = false,
  defaultFeedback = 'positive'
}) => {
  const [selectedTags, setSelectedTags] = React.useState<string[]>(defaultTags || []);
  const [notes, setNotes] = React.useState<string>(defaultNotes || '');
  const [selectedFeedback, setSelectedFeedback] = React.useState<'positive' | 'negative'>(defaultFeedback);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const isNeutral = showFeedbackSelector;
  const title = isNeutral
    ? 'Share your feedback'
    : (notesOnly ? 'Tell us more' : 'Tell us what went wrong');
  const helperText = isNeutral
    ? 'What worked well or could be improved? Any detail helps.'
    : (notesOnly
      ? 'What did you find helpful about this response? Is there anything that could improve?'
      : 'Try to provide any relevant context — was this a follow up response? How long had the conversation been? What specifically seemed to cause the issue?');
  const placeholderText = isNeutral
    ? 'Share what worked well or what could be improved'
    : (notesOnly
      ? 'Share any details you loved or ideas to make it even better'
      : 'Briefly describe the issue or what would make this better');

  React.useEffect(() => {
    if (isOpen) {
      setSelectedTags(defaultTags || []);
      setNotes(defaultNotes || '');
      setSelectedFeedback(defaultFeedback);
      setIsSubmitting(false);
    }
  }, [isOpen, defaultTags, defaultNotes, defaultFeedback]);

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit(selectedTags, notes.trim(), selectedFeedback);
      setIsSubmitting(false);
    } catch (e) {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-xl">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {showFeedbackSelector && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${
                  selectedFeedback === 'positive'
                    ? 'border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-200'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
                onClick={() => setSelectedFeedback('positive')}
                aria-pressed={selectedFeedback === 'positive'}
              >
                <ThumbsUp className="w-4 h-4" />
                Helpful
              </button>
              <button
                type="button"
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${
                  selectedFeedback === 'negative'
                    ? 'border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-200'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
                onClick={() => setSelectedFeedback('negative')}
                aria-pressed={selectedFeedback === 'negative'}
              >
                <ThumbsDown className="w-4 h-4" />
                Not helpful
              </button>
            </div>
          )}
          <div className="text-xs text-gray-600 dark:text-gray-300">
            {helperText}
          </div>
          {!notesOnly && (
            <div>
              <div className="text-sm text-gray-700 dark:text-gray-200 mb-2">Optional tags</div>
              <div className="flex flex-wrap gap-2">
                {allowedTags.map(tag => {
                  const checked = selectedTags.includes(tag);
                  const id = `tag-${tag.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`;
                  return (
                    <label
                      key={tag}
                      htmlFor={id}
                      role="checkbox"
                      aria-checked={checked}
                      tabIndex={0}
                      onClick={() => toggleTag(tag)}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          toggleTag(tag);
                        }
                      }}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm border transition-colors cursor-pointer select-none pointer-events-auto ${checked ? 'bg-teal-600 text-white border-teal-600' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50'}`}
                    >
                      <input
                        id={id}
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() => toggleTag(tag)}
                      />
                      {checked && <span className="mr-1">✓</span>}
                      {tag}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-200 mb-1">Details (optional)</label>
            <textarea
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 min-h-[100px]"
              placeholder={placeholderText}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          
          {/* Screenshot email note */}
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            Something to show us? You can send us screenshots at{' '}
            <a 
              href="mailto:askaldouat@unifirst.com?subject=Ask%20Aldo%20Feedback%20%2D%20Screenshots" 
              className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 underline font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              askaldouat@unifirst.com
            </a>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-2">
          <button
            type="button"
            className="px-4 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-xl text-sm bg-teal-500 hover:bg-teal-600 text-white disabled:opacity-60"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;


